/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid } from '@coursera/cds-core';

import { useConsumerMyPageHeader } from 'bundles/composable-page-header-consumer/contexts/ConsumerMyPageHeaderContext';
import EnterpriseOrgScopedSearchBarGridItem from 'bundles/composable-page-header-enterprise/components/EnterpriseOrgScopedSearchBarGridItem';
import {
  getEnterpriseOrgLogo,
  getEnterpriseOrgLogoLabel,
  getLinkForOrgHome,
} from 'bundles/composable-page-header-enterprise/utils/enterpriseLogoUtils';
import MainNavigationWrapper from 'bundles/composable-page-header/components/MainNavigationWrapper';
import LanguageDropdownLegacyGridItem from 'bundles/composable-page-header/components/grid-items/LanguageDropdownLegacyGridItem';
import NotificationsLegacyGridItem from 'bundles/composable-page-header/components/grid-items/NotificationsLegacyGridItem';
import CoachStudioGridItem, {
  showCoachStudio,
} from 'bundles/composable-page-header/components/grid-items/coach/CoachStudioGridItem';
import HomeLogoGridItemModule from 'bundles/composable-page-header/components/grid-items/home-logo/HomeLogoGridItemModule';
import DefaultAccountDropdownGridItemRenderer from 'bundles/composable-page-header/module-renderers/DefaultAccountDropdownGridItemRenderer';

const styles = {
  flexGrow: css`
    flex-grow: 1;
  `,
};

type Props = {
  showDiscoveryElements?: boolean;
};

const OrgScopedEnterpriseLearnerOnConsumerDesktopMainNav = ({ showDiscoveryElements = true }: Props) => {
  const {
    enrolledEnterprisePrograms,
    chosenDegree,
    chosenEnterpriseProgram,
    naptimeChosenOrganization,
    naptimeOrganizationsWithProgramEnrollments,
    hasEnrolledPrograms,
    naptimeManagedThirdPartyOrganizations,
    hasCheatingIncidents,
    canViewEducatorAdminLink,
    isCourseraPlusLearner,
    courseraPlusSubscriptionCycle,
    enrolledDegreePrograms,
  } = useConsumerMyPageHeader();

  if (!naptimeChosenOrganization) {
    return null;
  }

  return (
    <MainNavigationWrapper>
      <Grid item md={8} container alignItems="center" spacing={16}>
        <Grid item>
          <HomeLogoGridItemModule
            href={getLinkForOrgHome(naptimeChosenOrganization)}
            logos={[getEnterpriseOrgLogo(naptimeChosenOrganization)]}
            ariaLabel={getEnterpriseOrgLogoLabel(naptimeChosenOrganization)}
          />
        </Grid>
        {showDiscoveryElements && (
          <Grid item css={styles.flexGrow}>
            <EnterpriseOrgScopedSearchBarGridItem
              thirdPartyOrganizationSlug={naptimeChosenOrganization?.slug}
              enrolledEnterprisePrograms={enrolledEnterprisePrograms}
            />
          </Grid>
        )}
      </Grid>
      <Grid item container md={4} alignItems="center" justifyContent="flex-end">
        <LanguageDropdownLegacyGridItem
          currentThirdPartyOrgId={chosenEnterpriseProgram?.naptimeThirdPartyOrganization?.id}
        />
        <Grid item>
          <NotificationsLegacyGridItem />
        </Grid>
        {showCoachStudio() && (
          <Grid item>
            <CoachStudioGridItem />
          </Grid>
        )}
        <Grid item>
          <DefaultAccountDropdownGridItemRenderer
            hasEnrolledPrograms={!!hasEnrolledPrograms}
            naptimeManagedThirdPartyOrganizations={naptimeManagedThirdPartyOrganizations}
            hasCheatingIncidents={!!hasCheatingIncidents}
            canViewEducatorAdminLink={!!canViewEducatorAdminLink}
            isCourseraPlusLearner={!!isCourseraPlusLearner}
            courseraPlusSubscriptionCycle={courseraPlusSubscriptionCycle}
            currentOrganization={naptimeChosenOrganization}
            currentDegree={chosenDegree}
            organizations={naptimeOrganizationsWithProgramEnrollments}
            degrees={enrolledDegreePrograms}
          />
        </Grid>
      </Grid>
    </MainNavigationWrapper>
  );
};

export default OrgScopedEnterpriseLearnerOnConsumerDesktopMainNav;
